import React from 'react';
import classNames from 'classnames';
import { ChevronDownIcon, MenuButtonIcon } from '../icons/TourIcons';
import type { PaymentsTransaction } from '../core/Types';
import styles from '../stylesheets/transactionPanel.module.scss';

export type TransactionKind = 'invoice' | 'invoicePayment';

export type TransactionItem = PaymentsTransaction;

interface TransactionPanelProps {
  transaction: TransactionItem;
  defaultExpanded?: boolean;
  onInteraction?: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);

const TransactionPanel = ({
  transaction,
  defaultExpanded = false,
  onInteraction,
}: TransactionPanelProps) => {
  const {
    kind,
    date,
    number,
    amount,
    balance,
    dueDate,
    outstanding,
    statusLabel,
  } = transaction;

  const typeLabel = kind === 'invoicePayment' ? 'Rent - Payment' : `Rent`;
  const resolvedStatus =
    statusLabel ?? (kind === 'invoicePayment' ? 'Applied' : 'Outstanding');
  const isPaidInvoice =
    kind === 'invoice' && resolvedStatus.toLowerCase() === 'paid';
  const statusClass =
    kind === 'invoicePayment'
      ? styles.statusApplied
      : isPaidInvoice
        ? styles.statusPaid
        : styles.statusOutstanding;
  const amountClass =
    amount < 0 ? styles.amountNegative : styles.amountPositive;
  const titleClass =
    kind === 'invoicePayment'
      ? styles.titlePayment
      : isPaidInvoice
        ? styles.titlePaid
        : styles.titleInvoice;
  const dateLabel = kind === 'invoicePayment' ? 'Date' : 'Issued';
  const dueLabel = kind === 'invoicePayment' ? 'Due Date' : 'Due';
  const typeClasses =
    kind === 'invoicePayment'
      ? ['pm-transaction-type-payment', 'pm-transaction-type-invoice-payment']
      : ['pm-transaction-type-invoice'];
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const toggleExpanded = () => {
    onInteraction?.();
    setExpanded((prev) => !prev);
  };
  const summaryId = `transaction-summary-${transaction.id}`;
  const lineItems = transaction.lineItems ?? [];
  const appliedItems = transaction.appliedItems ?? [];
  const lineItemsTotal = lineItems.reduce(
    (total, item) => total + item.amount * item.quantity,
    0
  );

  return (
    <div
      className={classNames(
        styles.transactionPanel,
        'pm-transaction-panel',
        typeClasses
      )}
    >
      <button
        type="button"
        className={styles.headerRow}
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-controls={summaryId}
      >
        <div className={styles.field}>
          <div className={styles.label}>Date</div>
          <div className={styles.value}>{date}</div>
        </div>
        <div className={styles.typeField}>
          <span className={classNames(styles.typeTitle, titleClass)}>
            {typeLabel}
          </span>
          {resolvedStatus && (
            <span className={classNames(styles.statusPill, statusClass)}>
              {resolvedStatus}
            </span>
          )}
        </div>
        <div className={styles.field}>
          <div className={styles.label}>Number</div>
          <div className={styles.value}>{number}</div>
        </div>
        {dueDate && (
          <div className={styles.field}>
            <div className={styles.label}>Due Date</div>
            <div className={styles.value}>{dueDate || '--'}</div>
          </div>
        )}
        <div className={classNames(styles.field, styles.alignRight)}>
          <div className={styles.label}>Outstanding</div>
          <div className={styles.value}>{formatCurrency(outstanding ?? 0)}</div>
        </div>
        <div className={classNames(styles.field, styles.alignRight)}>
          <div className={styles.label}>Amount</div>
          <div className={classNames(styles.value, amountClass)}>
            {formatCurrency(amount)}
          </div>
        </div>
        <div className={classNames(styles.field, styles.alignRight)}>
          <div className={styles.label}>Balance</div>
          <div className={styles.value}>{formatCurrency(balance)}</div>
        </div>
      </button>

      <div
        className={styles.mobileCard}
        onClick={toggleExpanded}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div
          role="button"
          tabIndex={0}
          className={styles.mobileHeaderButton}
          aria-expanded={expanded}
          aria-controls={summaryId}
        >
          <div className={styles.mobileHeaderLeft}>
            <span
              className={classNames(
                styles.chevronButton,
                expanded ? styles.chevronExpanded : styles.chevronCollapsed
              )}
            >
              <ChevronDownIcon />
            </span>
            <span className={classNames(styles.mobileTitle, titleClass)}>
              {typeLabel}
            </span>
          </div>
          <div className={styles.mobileHeaderRight}>
            {resolvedStatus && (
              <span className={classNames(styles.statusPill, statusClass)}>
                {resolvedStatus}
              </span>
            )}
          </div>
        </div>
        <div className={styles.mobileDates}>
          <div className={styles.dateField}>
            <div className={styles.dateLabel}>{dateLabel}</div>
            <div className={styles.dateValueRow}>
              <span className={styles.dateValue}>{date}</span>
            </div>
          </div>
          {dueDate && (
            <div className={styles.dateField}>
              <div className={styles.dateLabel}>{dueLabel}</div>
              <div className={styles.dateValueRow}>
                <span className={styles.dateValue}>{dueDate || '--'}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      {expanded && kind === 'invoicePayment' && appliedItems.length > 0 && (
        <div className={styles.appliedSection}>
          {appliedItems.map((item) => {
            const qty = item.quantity ?? 1;
            const unitAmount = item.amount / qty;
            return (
              <div key={item.id} className={styles.appliedCard}>
                <div className={styles.appliedHeader}>
                  <div className={styles.appliedTitle}>{item.title}</div>
                  <div className={styles.appliedAmount}>
                    {formatCurrency(item.amount)}
                  </div>
                </div>
                <div className={styles.appliedSubtitle}>
                  {qty} × {formatCurrency(unitAmount)}
                </div>
                <div className={styles.appliedMeta}>
                  <div className={styles.appliedMetaRow}>
                    <span className={styles.appliedMetaLabel}>Item:</span>{' '}
                    {item.itemType ?? 'Rent'}
                  </div>
                  {item.appliedTo && (
                    <div className={styles.appliedMetaRow}>
                      <span className={styles.appliedMetaLabel}>
                        Applied to:
                      </span>{' '}
                      {item.appliedTo}
                    </div>
                  )}
                  {item.bankAccount && (
                    <div className={styles.appliedMetaRow}>
                      <span className={styles.appliedMetaLabel}>
                        Bank Account:
                      </span>{' '}
                      {item.bankAccount}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {expanded && kind !== 'invoicePayment' && lineItems.length > 0 && (
        <div className={styles.lineItemsSection}>
          {lineItems.map((item, index) => {
            const lineTotal = item.amount * item.quantity;
            return (
              <div key={item.id} className={styles.lineItemBlock}>
                <div className={styles.lineItemRow}>
                  <div className={styles.lineItemLeft}>
                    <div className={styles.lineItemTitle}>{item.itemName}</div>
                    <div className={styles.lineItemSite}>
                      {item.site || '-'}
                    </div>
                    <div className={styles.lineItemMeta}>
                      {item.company || '-'}
                    </div>
                    <div className={styles.lineItemMeta}>
                      {item.description || '-'}
                    </div>
                  </div>
                  <div className={styles.lineItemRight}>
                    <div className={styles.lineItemAmount}>
                      {formatCurrency(lineTotal)}
                    </div>
                    <div className={styles.lineItemQuantity}>
                      {item.quantity} x {formatCurrency(item.amount)}
                    </div>
                  </div>
                </div>
                {index < lineItems.length - 1 && (
                  <div className={styles.lineItemDivider} />
                )}
              </div>
            );
          })}
          <div className={styles.lineItemsTotalRow}>
            <span className={styles.lineItemsTotalLabel}>Total:</span>
            <span className={styles.lineItemsTotalValue}>
              {formatCurrency(lineItemsTotal)}
            </span>
          </div>
        </div>
      )}
      {expanded && (
        <div className={styles.mobileSummary} id={summaryId}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Outstanding:</span>
            <span className={styles.summaryValue}>
              {formatCurrency(outstanding ?? 0)}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Amount:</span>
            <span
              className={classNames(
                styles.summaryValue,
                amount < 0
                  ? styles.summaryValueNegative
                  : styles.summaryValuePositive
              )}
            >
              {formatCurrency(amount)}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Balance:</span>
            <span className={styles.summaryValue}>
              {formatCurrency(balance)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionPanel;
